import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { menu } from "./navegacao";
import { filtrarMenuAcessivel, type ItemMenuAcessivel } from "./menuAcesso";
import { useAuth } from "../auth/AuthContext";
import { listarEmpresasAtivasDaOrganizacao } from "../api/empresas";
import { AvatarCadastro } from "../components/tabela/AvatarCadastro";
import { BancoImage } from "../components/ui/BancoImage";
import { rolarPrincipalParaTopo } from "./scrollToTop";
import { useConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";
import { useMarcasEmpresa } from "../theme/useMarcasEmpresa";
import { useIdioma } from "../i18n/IdiomaProvider";
import styles from "./Sidebar.module.css";
import { abrirSeletorEmpresa } from "./eventosLayout";

interface SidebarProps {
  recolhido: boolean;
  buscaMenu?: string;
  onAbrirMenu?: () => void;
  onFecharMenu?: () => void;
  onReiniciarTour?: () => void;
  onAbrirChatCliente?: () => void;
  chatClienteAberto?: boolean;
}

const IconeChevron = () => (
  <svg viewBox="0 0 16 16" fill="none" width="14" height="14" aria-hidden="true" className={styles.chevron}>
    <path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function normalizar(texto: string): string {
  return texto.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function Sidebar({ recolhido, buscaMenu = "", onAbrirMenu, onAbrirChatCliente, chatClienteAberto = false }: SidebarProps) {
  const { traduzir } = useIdioma();
  const { usuario } = useAuth();
  const acessoRestrito = false;
  const configuracoes = useConfiguracoesSistemaPublicas();
  const location = useLocation();
  const navegar = useNavigate();
  const { logoUrl, marcaRecolhidaUrl } = useMarcasEmpresa();
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});
  const [empresaAtual, setEmpresaAtual] = useState<{
    nome: string;
    logoUrl: string;
    cnpj: string;
    status: string;
  } | null>(null);
  const cardEmpresaRef = useRef<HTMLButtonElement>(null);

  const codOrganizacaoAtiva = String(usuario?.selectedCodOrg || usuario?.codOrg || "");

  useEffect(() => {
    if (!codOrganizacaoAtiva || codOrganizacaoAtiva === "todos") {
      setEmpresaAtual(null);
      return;
    }
    let ativo = true;
    void listarEmpresasAtivasDaOrganizacao(codOrganizacaoAtiva)
      .then((lista) => {
        if (!ativo) return;
        const atual = lista.find((item) => String(item.codigo) === String(usuario?.codEmpresa || ""));
        setEmpresaAtual(
          atual
            ? {
                nome: atual.nome,
                logoUrl: atual.logoUrl || "",
                cnpj: atual.cnpj || "",
                status: atual.status || "ATIVO",
              }
            : null
        );
      })
      .catch(() => {
        if (ativo) setEmpresaAtual(null);
      });
    return () => {
      ativo = false;
    };
  }, [codOrganizacaoAtiva, usuario?.codEmpresa]);

  function abrirMenuRecolhido() {
    onAbrirMenu?.();
  }

  function navegarComMenuAberto(rota: string) {
    abrirMenuRecolhido();
    rolarPrincipalParaTopo("smooth");
    window.setTimeout(() => navegar(rota), 50);
  }

  const menuVisivel = useMemo<ItemMenuAcessivel[]>(() => {
    const itens = filtrarMenuAcessivel(menu, configuracoes, usuario);
    if (!acessoRestrito) return itens;
    return itens.map((item) => ({
      ...item,
      bloqueado: true,
      subitens: item.subitens?.map((subitem) => ({ ...subitem, bloqueado: true })),
    }));
  }, [configuracoes, usuario]);

  useEffect(() => {
    const grupoAtivo = menuVisivel.find((item) =>
      item.subitens?.some((subitem) => location.pathname.startsWith(subitem.rota))
    );
    if (grupoAtivo) {
      setAbertos((atual) => ({ ...atual, [grupoAtivo.rotulo]: true }));
    }
  }, [location.pathname, menuVisivel]);

  const itensFiltrados = useMemo<ItemMenuAcessivel[]>(() => {
    const q = normalizar(buscaMenu.trim());
    if (!q) return menuVisivel;

    return menuVisivel
      .map((item) => {
        if ([item.rotulo, traduzir(item.rotulo)].some((rotulo) => normalizar(rotulo).includes(q))) return item;
        const subitens = item.subitens?.filter((subitem) =>
          [subitem.rotulo, traduzir(subitem.rotulo)].some((rotulo) => normalizar(rotulo).includes(q))
        );
        return subitens?.length ? { ...item, subitens } : null;
      })
      .filter((item): item is ItemMenuAcessivel => item !== null);
  }, [buscaMenu, menuVisivel, traduzir]);

  function alternarGrupo(rotulo: string) {
    setAbertos((atual) => ({ ...atual, [rotulo]: !atual[rotulo] }));
  }

  const imgRecolhido = recolhido ? marcaRecolhidaUrl : logoUrl;

  return (
    <aside data-tour="sidebar-menu" className={`${styles.sidebar} ${recolhido ? styles.recolhido : ""}`}>
      <div className={styles.marca}>
        {recolhido ? (
          imgRecolhido ? (
            <BancoImage className={styles.logoIcone} src={imgRecolhido} alt="Logo" />
          ) : (
            <BancoImage className={styles.logoIcone} alt="Logo" />
          )
        ) : (
          <BancoImage className={styles.logoImg} src={logoUrl} alt="Logo" />
        )}
      </div>

      <nav className={styles.nav}>
        {itensFiltrados.map((item) => {
          if (item.bloqueado && item.subitens) {
            return (
              <button key={item.rotulo} type="button" className={`${styles.item} ${styles.itemGrupo} ${styles.bloqueado}`} disabled>
                <span className={styles.itemIcone}><item.Icone /></span>
                <span className={styles.itemRotulo}>{traduzir(item.rotulo)}</span>
              </button>
            );
          }

          if (!item.subitens) {
            if (item.bloqueado) {
              return (
                <button key={item.rotulo} type="button" className={`${styles.item} ${styles.bloqueado}`} disabled>
                  <span className={styles.itemIcone}><item.Icone /></span>
                  <span className={styles.itemRotulo}>{traduzir(item.rotulo)}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={item.rotulo}
                to={item.rota ?? "/"}
                end={item.rota === "/"}
                className={({ isActive }) => `${styles.item} ${isActive ? styles.ativo : ""}`}
                onClick={(event) => {
                  rolarPrincipalParaTopo("smooth");
                  if (recolhido && item.rota) {
                    event.preventDefault();
                    navegarComMenuAberto(item.rota);
                  }
                }}
              >
                <span className={styles.itemIcone}><item.Icone /></span>
                <span className={styles.itemRotulo}>{traduzir(item.rotulo)}</span>
              </NavLink>
            );
          }

          const buscando = buscaMenu.trim() !== "";
          const aberto = buscando || abertos[item.rotulo];
          const grupoAtivo = item.subitens.some((subitem) => location.pathname.startsWith(subitem.rota));

          return (
            <div key={item.rotulo} className={styles.grupo}>
              <button
                type="button"
                className={`${styles.item} ${styles.itemGrupo} ${grupoAtivo ? styles.ativo : ""}`}
                onClick={() => {
                  if (recolhido) {
                    abrirMenuRecolhido();
                    setAbertos((atual) => ({ ...atual, [item.rotulo]: true }));
                  } else {
                    alternarGrupo(item.rotulo);
                  }
                }}
                aria-expanded={aberto}
              >
                <span className={styles.itemIcone}><item.Icone /></span>
                <span className={styles.itemRotulo}>{traduzir(item.rotulo)}</span>
                {!recolhido && (
                  <span className={`${styles.chevronWrap} ${aberto ? styles.chevronAberto : ""}`}>
                    <IconeChevron />
                  </span>
                )}
              </button>

              {!recolhido && aberto && (
                <div className={styles.submenu}>
                  {item.subitens.map((subitem) =>
                    subitem.bloqueado ? (
                      <button key={subitem.rota} type="button" className={`${styles.subitem} ${styles.bloqueado}`} disabled>
                        {traduzir(subitem.rotulo)}
                      </button>
                    ) : subitem.rota === "/area-cliente/chat" ? (
                      <button
                        key={subitem.rota}
                        type="button"
                        className={`${styles.subitem} ${chatClienteAberto ? styles.subAtivo : ""}`}
                        aria-pressed={chatClienteAberto}
                        onClick={() => onAbrirChatCliente?.()}
                      >
                        {traduzir(subitem.rotulo)}
                      </button>
                    ) : (
                      <NavLink
                        key={subitem.rota}
                        to={subitem.rota}
                        target={subitem.novaAba ? "_blank" : undefined}
                        rel={subitem.novaAba ? "noopener noreferrer" : undefined}
                        end
                        className={({ isActive }) => `${styles.subitem} ${isActive ? styles.subAtivo : ""}`}
                        onClick={() => { if (!subitem.novaAba) rolarPrincipalParaTopo("smooth"); }}
                      >
                        {traduzir(subitem.rotulo)}
                      </NavLink>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className={styles.rodapeOrganizacao}>
        <button
          ref={cardEmpresaRef}
          type="button"
          data-tour="sidebar-company"
          className={styles.cardOrganizacao}
          aria-label={traduzir("Trocar de empresa")}
          aria-haspopup="listbox"
          disabled={acessoRestrito}
          onClick={() => {
            if (cardEmpresaRef.current) abrirSeletorEmpresa(cardEmpresaRef.current);
          }}
        >
          {!recolhido && <span className={styles.cardOrganizacaoSelo}>{traduzir("Atual")}</span>}
          <div className={styles.marcaEmpresaAtual} aria-hidden="true">
            {recolhido ? (
              <span className={styles.iconeOrganizacao}><Building2 size={21} strokeWidth={1.8} /></span>
            ) : empresaAtual?.logoUrl ? (
              <AvatarCadastro nome={empresaAtual.nome} foto={empresaAtual.logoUrl} tamanho={34} quadrado />
            ) : (
              <span className={styles.iconeOrganizacao}><Building2 size={18} strokeWidth={1.8} /></span>
            )}
          </div>
          {!recolhido && (
            <span className={styles.infoEmpresaAtual}>
              <span className={styles.textoOrganizacao} data-i18n-db="true">
                {empresaAtual?.nome || configuracoes.fantasia}
              </span>
              {empresaAtual?.cnpj && (
                <span className={styles.cnpjEmpresaAtual}>
                  <span>{traduzir("CNPJ")}:</span>{" "}
                  <span data-i18n-db="true">{empresaAtual.cnpj}</span>
                </span>
              )}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
